import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import {
  COMPLETED_BY_ROBOTO,
  type SessionRecord,
} from "@/shared/domain/sessions";

import styles from "./SessionDetailsTab.module.css";

/** How the session will be marked complete, or when and by whom it was. */
export function CompletionDescription({ session }: { session: SessionRecord }) {
  if (session.status === "complete") {
    return <CompletedDescription session={session} />;
  }
  if (session.completion_policy === null) {
    return <>When someone marks it complete</>;
  }
  const inactivity = inactivityPhrase(
    session.completion_policy.inactivity_minutes,
  );
  if (session.completes_at === null) {
    return <>Completes automatically {inactivity} after a file is last added</>;
  }
  return (
    <>
      Completes automatically at{" "}
      <FormattedDateTime value={session.completes_at} timeZone /> unless another
      file is added ({inactivity} without a new file)
    </>
  );
}

function CompletedDescription({ session }: { session: SessionRecord }) {
  if (session.completed_at === null) {
    return <>Completed</>;
  }
  if (
    session.completed_by === COMPLETED_BY_ROBOTO ||
    session.completed_by === null
  ) {
    const policy = session.completion_policy;
    return (
      <>
        Completed <FormattedDateTime value={session.completed_at} timeZone />
        {session.completed_by === COMPLETED_BY_ROBOTO && " automatically"}
        {session.completed_by === COMPLETED_BY_ROBOTO &&
          policy !== null &&
          ` after ${inactivityPhrase(policy.inactivity_minutes)} without a new file`}
      </>
    );
  }
  return (
    <>
      Completed <FormattedDateTime value={session.completed_at} timeZone />
      {" by "}
      <RobotoPrincipalUnderlineLink
        className={styles.inlinePrincipal}
        principal={session.completed_by}
      />
    </>
  );
}

/** "15 minutes", "2 hours", "1 day 6 hours". */
function inactivityPhrase(minutes: number): string {
  const units: [number, string, string][] = [
    [Math.floor(minutes / (24 * 60)), "day", "days"],
    [Math.floor((minutes % (24 * 60)) / 60), "hour", "hours"],
    [minutes % 60, "minute", "minutes"],
  ];
  const parts = units
    .filter(([count]) => count > 0)
    .map(([count, one, many]) => `${count} ${count === 1 ? one : many}`);
  return parts.length === 0 ? "0 minutes" : parts.join(" ");
}
